import { inject, Injectable, signal } from '@angular/core';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { CollectionsMode } from '@anduin/config';
import { buildActiveFilters } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { CommonCollectionsState } from '../state';
import { injectCollectionsTracking } from './collections-tracking.providers';
import {
  CollectionsBaseMetadata,
  CollectionsModeLabel,
  CollectionsSettingsVersion,
  CollectionsStatementsContext,
  CollectionsStatementsStep,
  CollectionsStepMetadataInput,
  CollectionsWorkflowStep,
  CollectionsWorkflowStepMetaDataMap,
} from './collections-workflow.models';

const STATEMENTS_STEPS = new Set<string>(Object.values(CollectionsStatementsStep));

/**
 * Per-container tracking facade. Wraps the shared `TrackWorkflowService` and stamps base
 * context onto every event:
 *
 *   - `settingsVersion` — derived from `ENABLE_COLLECTIONS_V2` (signal-based so dev-toolbar
 *     overrides take effect, per the project's LaunchDarkly rules).
 *   - `collectionsMode` — derived from `CommonCollectionsState.getCollectionsMode`.
 *   - `activeFilters` — added only for Statements steps (partner filter + search are
 *     meaningful only on the Statements dashboard).
 *
 * Registered per-container (`providers: [CollectionsTrackService]`) on
 * `CollectionsStatementsContainerComponent` and `CollectionsSettingsComponent` so each
 * surface's setter-driven state stays isolated.
 */
@Injectable()
export class CollectionsTrackService {
  private readonly tracker = injectCollectionsTracking();
  private readonly store = inject(Store);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  private readonly _collectionsV2Enabled = this.launchDarklyService.getFeatureFlagSignal(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2);

  // Statements container pushes the active value of each filter dimension via the setters
  // below. resolveActiveFilters() tokenises whatever is currently set into the
  // comma-bracketed string stamped on Statements events only.
  private readonly _partnerFilter = signal<string>('');
  private readonly _searchValue = signal<string>('');

  setPartnerFilter(value: string | null | undefined): void {
    this._partnerFilter.set(value ?? '');
  }

  setSearchValue(value: string | null | undefined): void {
    this._searchValue.set(value ?? '');
  }

  trackWorkflow<E extends CollectionsWorkflowStep>(step: E, metadata?: CollectionsStepMetadataInput<E>): void {
    const context = this.isStatementsStep(step) ? this.resolveStatementsContext() : this.resolveBaseMetadata();
    const merged = { ...context, ...(metadata ?? {}) } as CollectionsWorkflowStepMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  private isStatementsStep(step: CollectionsWorkflowStep): boolean {
    return STATEMENTS_STEPS.has(step);
  }

  private resolveBaseMetadata(): CollectionsBaseMetadata {
    return {
      settingsVersion: this.resolveSettingsVersion(),
      collectionsMode: this.resolveCollectionsMode(),
    };
  }

  private resolveStatementsContext(): CollectionsStatementsContext {
    return {
      ...this.resolveBaseMetadata(),
      activeFilters: this.resolveActiveFilters(),
    };
  }

  private resolveActiveFilters(): string {
    const tokens: string[] = [];
    if (this._partnerFilter().trim().length > 0) {
      tokens.push('partner');
    }
    if (this._searchValue().trim().length > 0) {
      tokens.push('search');
    }
    return buildActiveFilters(tokens);
  }

  private resolveSettingsVersion(): CollectionsSettingsVersion {
    return this._collectionsV2Enabled() ? 'v2' : 'v1';
  }

  private resolveCollectionsMode(): CollectionsModeLabel {
    return this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode) === CollectionsMode.CONTACT ? 'CONTACT' : 'CLIENT';
  }
}
